import { BaseService, BaseServiceWithFilter } from '../services/base.service';
import { Type } from '@angular/core';
import { BaseModel } from '@cl/shared/models/base/base-model.model';

import { NzModalService } from 'ng-zorro-antd/modal';
import { BaseUpsertComponent } from './base-upsert-component';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { ModalText } from './modal-text.model';
import { BaseFilterModel } from '@cl/shared/models/base/base-filter-model.model';
import { lastValueFrom } from 'rxjs';
import { BaseTableResult } from '@cl/shared/models/base/base-table-result.model';
import { CommandResult } from '@cl/shared/models';

export abstract class BaseList<
    T extends BaseModel,
    TService extends BaseService<T>,
    TUpsertComponent extends Type<BaseUpsertComponent<T, TService>>,
> {
    total: number = 1;
    listObjects: T[] = [];
    loading: boolean = true;
    pageSize: number = 25;
    pageSizeOptions: number[] = [10, 25, 50, 100];
    pageIndex: number = 1;
    sortField: string = null;
    sortOrder: string = null;
    modalTexts: ModalText = {
        addObjectTitle: 'Objekta pievienošana',
        editObjectTitle: 'Objekta labošana',
        deleteConfirmQuestion: 'Vai tiešām vēlaties dzēst?',
        deleteSuccess: 'Objekts veiksmīgi dzēsts!',
        deleteFailed: 'Kļūda dzēšot objektu!',
        listDataRetrieveFailed: 'Kļūda iegūstot datus',
    };

    // Token of the latest load. Every new load (also one a derived class answers without a request) replaces it, so a
    // response or an error of an earlier load no longer changes the list state (rows, total, sorting, messages, loading).
    private currentLoad: object = {};

    constructor(
        protected modalService: NzModalService,
        protected service: TService,
        protected upsertComponent: TUpsertComponent,
        protected messageService: NzMessageService,
    ) {}

    abstract loadListObjects(): Promise<void>;
    abstract onQueryParamsChange(params: NzTableQueryParams): Promise<void>;

    protected startLoad(): object {
        return (this.currentLoad = {});
    }

    protected isCurrentLoad(load: object): boolean {
        return this.currentLoad === load;
    }

    addListObject(): void {
        const modal = this.modalService.create({
            nzTitle: this.modalTexts.addObjectTitle,
            nzContent: this.upsertComponent,
        });

        modal.afterOpen.subscribe(() => {
            modal.getContentComponent().initForm();
            modal.getContentComponent().afterInit();
        });

        modal.afterClose.subscribe(async (reload) => {
            if (reload) {
                await this.loadListObjects();
            }
        });
    }

    async editListObject(obj: T): Promise<void> {
        const editableObj = await (await this.service.getSingle(obj.id)).data;
        const modal = this.modalService.create({
            nzTitle: this.modalTexts.editObjectTitle,
            nzContent: this.upsertComponent,
        });

        modal.afterOpen.subscribe(() => {
            modal.getContentComponent().setEditMode(editableObj);
        });

        modal.afterClose.subscribe(async (reload) => {
            if (reload) {
                await this.loadListObjects();
            }
        });
    }

    async deleteListObject(id: number): Promise<void> {
        this.modalService.confirm({
            nzTitle: this.modalTexts.deleteConfirmQuestion,
            nzCancelText: 'Atcelt',
            nzOnOk: async () => {
                try {
                    this.loading = true;
                    await this.service.delete(id);
                    await this.loadListObjects();
                    this.loading = false;
                    this.messageService.success(this.modalTexts.deleteSuccess);
                } catch (ex) {
                    this.loading = false;
                    console.error(ex);
                    if (ex.error?.errors) {
                        ex.error.errors.forEach((error) => {
                            this.messageService.error(error);
                        });
                        return;
                    }

                    this.messageService.error(this.modalTexts.deleteFailed);
                    return;
                }
            },
        });
    }
}

export abstract class BaseListComponent<
    T extends BaseModel,
    TService extends BaseService<T>,
    TUpsertComponent extends Type<BaseUpsertComponent<T, TService>>,
> extends BaseList<T, TService, TUpsertComponent> {
    constructor(
        protected modalService: NzModalService,
        protected service: TService,
        protected upsertComponent: TUpsertComponent,
        protected messageService: NzMessageService,
    ) {
        super(modalService, service, upsertComponent, messageService);
    }

    // The load starts here, before the (overridable) loader: a derived loader that answers without a request still
    // cancels the earlier load.
    async loadListObjects(): Promise<void> {
        this.startLoad();
        await this.loadPaginatedOrderedDataFromServer(this.pageIndex, this.pageSize, this.sortField, this.sortOrder);
    }

    async onQueryParamsChange(params: NzTableQueryParams): Promise<void> {
        const { pageSize, pageIndex, sort } = params;
        this.pageSize = pageSize;
        const currentSort = sort.find((item) => item.value !== null);
        const sortField = (currentSort && currentSort.key) || null;
        const sortOrder = (currentSort && currentSort.value) || null;
        this.startLoad();
        await this.loadPaginatedOrderedDataFromServer(pageIndex, pageSize, sortField, sortOrder);
    }

    // Also called directly by derived classes, so the loader starts its own load; the result is applied only while it
    // is still the latest one.
    async loadPaginatedOrderedDataFromServer(pageIndex: number, pageSize: number, sortField: string | null, sortOrder: string | null): Promise<void> {
        const load = this.startLoad();
        try {
            this.loading = true;
            const result: CommandResult<BaseTableResult<T>> = await lastValueFrom(
                this.service.getAllServerSidePaginatedOrdered(pageIndex, pageSize, sortField, sortOrder),
            );
            if (!this.isCurrentLoad(load)) return;
            this.total = result.data.total;
            this.listObjects = result.data.rows;
            this.pageSize = pageSize;
            this.sortField = sortField;
            this.sortOrder = sortOrder;
            this.loading = false;
        } catch (ex) {
            if (!this.isCurrentLoad(load)) return;
            this.loading = false;
            console.error(ex);
            if (ex.error?.errors) {
                ex.error.errors.forEach((error) => {
                    this.messageService.error(error);
                });
                return;
            }

            this.messageService.error(this.modalTexts.listDataRetrieveFailed);
            return;
        }
    }
}

export abstract class BaseListComponentWithFilter<
    T extends BaseModel,
    TService extends BaseServiceWithFilter<T, TFilter>,
    TFilter extends BaseFilterModel,
    TUpsertComponent extends Type<BaseUpsertComponent<T, TService>>,
> extends BaseList<T, TService, TUpsertComponent> {
    filterObj: TFilter;

    constructor(
        protected modalService: NzModalService,
        protected service: TService,
        protected upsertComponent: TUpsertComponent,
        protected messageService: NzMessageService,
    ) {
        super(modalService, service, upsertComponent, messageService);
    }

    // The load starts here, before the (overridable) loader: a derived loader that answers without a request still
    // cancels the earlier load.
    async loadListObjects(): Promise<void> {
        this.startLoad();
        await this.loadPaginatedOrderedFilteredDataFromServer(this.pageIndex, this.pageSize, this.sortField, this.sortOrder, this.filterObj);
    }

    async onQueryParamsChange(params: NzTableQueryParams): Promise<void> {
        const { pageSize, pageIndex, sort } = params;
        this.pageSize = pageSize;
        const currentSort = sort.find((item) => item.value !== null);
        const sortField = (currentSort && currentSort.key) || null;
        const sortOrder = (currentSort && currentSort.value) || null;
        this.startLoad();
        await this.loadPaginatedOrderedFilteredDataFromServer(pageIndex, pageSize, sortField, sortOrder, this.filterObj);
    }

    // Also called directly by derived classes, so the loader starts its own load; the result is applied only while it
    // is still the latest one.
    protected async loadPaginatedOrderedFilteredDataFromServer(
        pageIndex: number,
        pageSize: number,
        sortField: string | null,
        sortOrder: string | null,
        filterObj: TFilter,
    ): Promise<void> {
        const load = this.startLoad();
        try {
            this.loading = true;
            const serversideResult = await lastValueFrom(
                this.service.getAllServerSidePaginatedOrderedFiltered(pageIndex, pageSize, sortField, sortOrder, filterObj),
            );
            if (!this.isCurrentLoad(load)) return;
            this.total = serversideResult.data.total;
            this.listObjects = serversideResult.data.rows;
            this.pageSize = pageSize;
            this.sortField = sortField;
            this.sortOrder = sortOrder;
            this.loading = false;
        } catch (ex) {
            if (!this.isCurrentLoad(load)) return;
            this.loading = false;
            console.error(ex);
            if (ex.error?.errors) {
                ex.error.errors.forEach((error) => {
                    this.messageService.error(error);
                });
                return;
            }

            this.messageService.error(this.modalTexts.listDataRetrieveFailed);
            return;
        }
    }
}
